import { money } from "@/lib/api";
import { NumberPlate } from "@/components/NumberPlate";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const m = String(iso).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(m)) { const [y, mo, d] = m.split("-"); return `${d}/${mo}/${y}`; }
  try { return new Date(iso).toLocaleDateString("en-GB", { timeZone: "Europe/London", day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return m; }
};

const STATUS_LABEL = { paid: "PAID", partial: "PART PAID", unpaid: "UNPAID" };
const STATUS_TONE = {
  paid: "bg-emerald-100 text-emerald-700",
  partial: "bg-amber-100 text-amber-700",
  unpaid: "bg-slate-200 text-slate-600",
};

/**
 * Fixed, professional A4 invoice document. Shared by the invoice detail page
 * (printable + PDF-matching) and the Business Settings live preview.
 */
export function InvoiceA4({ bundle, printable = false }) {
  const { invoice = {}, garage = {}, customer, vehicle, line_items = [], job } = bundle || {};
  const logoSrc = garage.logo_data_url || garage.logo_url;
  const vatEnabled = invoice.vat_enabled !== false && garage.vat_registered !== false;
  const subtotal = invoice.total_subtotal != null ? invoice.total_subtotal : ((invoice.total_net || 0) + (invoice.discount || 0));
  const discount = Number(invoice.discount || 0);
  const paid = Number(invoice.total_paid || 0);
  const balance = invoice.balance != null ? invoice.balance : ((invoice.total_gross || 0) - paid);
  const pstatus = invoice.payment_status || "unpaid";

  return (
    <div id={printable ? "print-area" : undefined} className="mx-auto max-w-3xl rounded-xl border border-border bg-white p-8 shadow-sm print:border-0 print:shadow-none sm:p-10" data-testid="invoice-doc">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          {logoSrc && <img src={logoSrc} alt="logo" className="h-12 w-12 object-contain" data-testid="invoice-logo" />}
          <div>
            <h2 className="font-display text-xl font-extrabold uppercase leading-tight text-slate-900">{garage.name || "Your Garage"}</h2>
            {garage.trading_name && <p className="text-[11px] uppercase tracking-wide text-slate-400">t/a {garage.trading_name}</p>}
            {garage.tagline && <p className="text-[11px] uppercase tracking-wide text-slate-400">{garage.tagline}</p>}
            <div className="mt-0.5 flex flex-wrap gap-x-3 text-[10px] text-slate-400">
              {garage.company_reg_number && <span>Co. Reg: {garage.company_reg_number}</span>}
              {garage.vat_registered && garage.vat_number && <span>VAT: {garage.vat_number}</span>}
            </div>
          </div>
        </div>
        <div className="text-right">
          <p className="font-display text-4xl font-extrabold uppercase tracking-tight text-[#252A31]">Invoice</p>
          {(garage.website || garage.email) && <p className="text-[11px] uppercase tracking-wide text-slate-400">{garage.website || garage.email}</p>}
        </div>
      </div>
      <div className="my-5 h-[2px] w-full bg-[#0F766E]" />

      {/* Invoice-to + meta */}
      <div className="flex items-start justify-between gap-6">
        <div>
          <p className="text-sm text-slate-500">Invoice to :</p>
          <p className="font-display text-lg font-bold text-slate-900">{customer?.name || "—"}</p>
          {customer?.company_name && <p className="text-sm text-slate-500">{customer.company_name}</p>}
          {customer?.address && <p className="text-sm text-slate-500">{customer.address}</p>}
          {customer?.phone && <p className="text-sm text-slate-500">{customer.phone}</p>}
          {customer?.email && <p className="text-sm text-slate-500">{customer.email}</p>}
          {vehicle?.vrm && (
            <div className="mt-2 flex items-center gap-2">
              <NumberPlate vrm={vehicle.vrm} size="sm" />
              <span className="text-sm text-slate-500">{vehicle.make} {vehicle.model}{vehicle.year ? ` · ${vehicle.year}` : ""}</span>
            </div>
          )}
        </div>
        <div className="shrink-0 text-right">
          <p className="text-base font-bold text-slate-900">Invoice no : {invoice.invoice_number}</p>
          <p className="text-sm text-slate-500">Date : {fmtDate(invoice.tax_point || invoice.created_at)}</p>
          <p className="text-sm text-slate-500">Due : {fmtDate(invoice.due_at)}</p>
          {job?.ref && <p className="text-sm text-slate-500">Ref : {job.ref}</p>}
          {(invoice.category_labels?.length ? invoice.category_labels.join(" · ") : invoice.category_label) && <p className="mt-1 text-sm font-bold uppercase tracking-wide text-brand-2" data-testid="invoice-doc-category">{invoice.category_labels?.length ? invoice.category_labels.join(" · ") : invoice.category_label}</p>}
          <div className="mt-1.5 flex justify-end">
            <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${STATUS_TONE[pstatus] || STATUS_TONE.unpaid}`} data-testid="invoice-doc-status">{STATUS_LABEL[pstatus] || "UNPAID"}</span>
          </div>
        </div>
      </div>

      {/* Table */}
      <table className="mt-6 w-full border-collapse text-sm">
        <thead>
          <tr className="border-b-2 border-[#0F766E] bg-[#F0F2F5] text-left text-xs font-bold uppercase tracking-wide text-[#252A31]">
            <th className="px-3 py-2.5 text-center">No</th>
            <th className="px-3 py-2.5">Description</th>
            <th className="px-3 py-2.5">Type</th>
            <th className="px-3 py-2.5 text-center">Qty</th>
            <th className="px-3 py-2.5 text-right">Unit</th>
            {vatEnabled && <th className="px-3 py-2.5 text-right">VAT</th>}
            <th className="px-3 py-2.5 text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {(() => {
            const colCount = vatEnabled ? 7 : 6;
            const hasCats = line_items.some((li) => li.category);
            const groups = [];
            const idx = {};
            line_items.forEach((li) => {
              const key = li.category || "_uncat";
              if (idx[key] == null) { idx[key] = groups.length; groups.push({ key, label: li.category_label || (key === "_uncat" ? "Other items" : key), items: [] }); }
              groups[idx[key]].items.push(li);
            });
            let n = 0;
            const rows = [];
            groups.forEach((g) => {
              if (hasCats) rows.push(
                <tr key={`g-${g.key}`} className="bg-[#EAF1F0]" data-testid={`invoice-doc-group-${g.key}`}>
                  <td colSpan={colCount} className="px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-[#0F766E]">{g.label}</td>
                </tr>
              );
              g.items.forEach((li) => {
                const i = n++;
                if ((li.kind || "").toLowerCase() === "note") {
                  rows.push(
                    <tr key={i} className="border-b border-slate-100" data-testid={`invoice-doc-row-${i}`}>
                      <td className="px-3 py-2.5 text-center text-slate-400" />
                      <td className="px-3 py-2.5 italic text-slate-600" colSpan={colCount - 1}>{li.description}</td>
                    </tr>
                  );
                  return;
                }
                const net = li.line_net != null ? li.line_net : (Number(li.qty || 0) * Number(li.unit_price || 0));
                const lvat = li.line_vat != null ? li.line_vat : (vatEnabled ? net * (invoice.vat_rate || 20) / 100 : 0);
                rows.push(
                  <tr key={i} className={`border-b border-slate-100 ${i % 2 === 1 ? "bg-[#F8FAFB]" : ""}`} data-testid={`invoice-doc-row-${i}`}>
                    <td className="px-3 py-2.5 text-center text-slate-500">{i + 1}</td>
                    <td className="px-3 py-2.5 text-[#252A31]">{li.description}</td>
                    <td className="px-3 py-2.5 text-xs uppercase text-slate-500">{li.kind_label || li.kind}</td>
                    <td className="px-3 py-2.5 text-center text-slate-700">{li.qty}</td>
                    <td className="px-3 py-2.5 text-right text-slate-700">{money(li.unit_price)}</td>
                    {vatEnabled && <td className="px-3 py-2.5 text-right text-slate-700">{money(lvat)}</td>}
                    <td className="px-3 py-2.5 text-right font-semibold text-[#252A31]">{money(net)}</td>
                  </tr>
                );
              });
            });
            if (line_items.length === 0) rows.push(
              <tr key="empty"><td colSpan={colCount} className="px-3 py-6 text-center text-sm text-slate-400">No line items yet.</td></tr>
            );
            return rows;
          })()}
        </tbody>
      </table>

      {/* Payment details (left) + single totals (right) — no duplicate grand-total band */}
      <div className="mt-6 flex flex-wrap items-start justify-between gap-6 break-inside-avoid">
        <div className="min-w-[14rem] text-sm text-slate-600" data-testid="invoice-doc-payment">
          <p className="mb-1.5 text-xs font-bold uppercase tracking-widest text-[#252A31]">Payment Details</p>
          {invoice.bank_name && <p data-testid="invoice-doc-bank-name">Bank Name : {invoice.bank_name}</p>}
          {invoice.bank_account_name && <p data-testid="invoice-doc-account-name">Account Name : {invoice.bank_account_name}</p>}
          {invoice.sort_code && <p data-testid="invoice-doc-sort-code">Sort Code : {invoice.sort_code}</p>}
          {invoice.account_number && <p data-testid="invoice-doc-account-number">Account Number : {invoice.account_number}</p>}
          {!invoice.bank_name && !invoice.sort_code && !invoice.account_number && garage.bank_details && <p>{garage.bank_details}</p>}
          {invoice.payment_terms && <p className="mt-1.5 text-xs text-slate-500"><span className="font-bold">Payment terms:</span> {invoice.payment_terms}</p>}
        </div>
        <div className="w-72 text-sm">
          <div className="flex justify-between py-1"><span className="text-slate-500">Subtotal</span><span className="font-semibold text-[#252A31]" data-testid="invoice-doc-subtotal">{money(subtotal)}</span></div>
          {discount > 0 && <div className="flex justify-between py-1" data-testid="invoice-doc-discount"><span className="text-slate-500">Discount</span><span className="font-semibold text-[#252A31]">- {money(discount)}</span></div>}
          {vatEnabled && <div className="flex justify-between py-1" data-testid="invoice-doc-tax"><span className="text-slate-500">VAT {invoice.vat_rate ?? 20}%</span><span className="font-semibold text-[#252A31]">{money(invoice.total_vat)}</span></div>}
          <div className="mt-1 flex justify-between border-t border-slate-200 py-1.5"><span className="font-bold text-[#252A31]">Total</span><span className="font-bold text-[#252A31]" data-testid="invoice-doc-total">{money(invoice.total_gross)}</span></div>
          {paid > 0 && <div className="flex justify-between py-1"><span className="text-slate-500">Amount Paid</span><span className="font-semibold text-emerald-600" data-testid="invoice-doc-paid">{money(paid)}</span></div>}
          <div className="mt-1 flex items-center justify-between rounded border-y-2 border-[#0F766E] bg-[#E7F3F1] px-3 py-2" data-testid="invoice-doc-grandtotal">
            <span className="text-sm font-bold uppercase tracking-wide text-[#252A31]">{paid > 0 ? "Balance Due" : "Total Due"}</span>
            <span className="font-display text-lg font-extrabold text-[#252A31]" data-testid="invoice-doc-balance">{money(paid > 0 ? balance : invoice.total_gross)}</span>
          </div>
        </div>
      </div>

      {invoice.notes && <p className="mt-5 text-sm italic text-slate-500" data-testid="invoice-doc-notes">{invoice.notes}</p>}

      <div className="my-6 h-px w-48 bg-slate-300" />
      <p className="font-display text-lg font-bold text-[#252A31]">{invoice.thank_you || "Thank you for business with us!"}</p>

      {/* Terms + signature */}
      <div className="mt-6 flex items-start justify-between gap-6 break-inside-avoid">
        <div className="max-w-sm">
          {invoice.terms && <><p className="font-bold text-[#252A31]">Terms &amp; Conditions</p><p className="mt-1 text-xs leading-relaxed text-slate-500">{invoice.terms}</p></>}
        </div>
        <div className="text-center">
          {invoice.signatory_name && <p className="font-display text-base font-bold text-[#252A31]">{invoice.signatory_name}</p>}
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Administrator</p>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 h-px w-full bg-slate-200" />
      {(invoice.footer || garage.invoice_footer) && <p className="mt-3 text-xs text-slate-500" data-testid="invoice-doc-footer">{invoice.footer || garage.invoice_footer}</p>}
      <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-slate-500">
        {garage.phone && <span>{garage.phone}</span>}
        {garage.email && <span>{garage.email}</span>}
        {garage.address && <span>{garage.address}</span>}
      </div>
    </div>
  );
}
